import { CommonModule } from '@angular/common';
import { Component, inject, signal } from '@angular/core';
import { ActivatedRoute, RouterModule } from '@angular/router';
// 3rd party
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';

// local
import {
  Card,
  FormErrorWrapper,
  FormLabel,
  LoadingIndicator,
} from '@aurrica-web/ui';
import { injectDispatch } from '@ngrx/signals/events';
import {
  AUTH_STORAGE_KEYS,
  authEvents,
  AuthStore,
  Login,
  LoginSchema,
} from '@aurrica-web/core-data';
import {
  FormField,
  FormRoot,
  form,
  validateStandardSchema,
} from '@angular/forms/signals';
import { faEye, faEyeSlash } from '@fortawesome/free-solid-svg-icons';
import { TranslocoDirective } from '@jsverse/transloco';
import { AuthFormHeader } from '../layout/auth-form-header';

@Component({
  selector: 'lib-auth-admin-login-form',
  imports: [
    CommonModule,
    RouterModule,
    FontAwesomeModule,
    Card,
    FormLabel,
    FormErrorWrapper,
    FormRoot,
    FormField,
    LoadingIndicator,
    TranslocoDirective,
    AuthFormHeader,
  ],
  template: `
    <ng-container *transloco="let t">
      <lib-ui-card
        class="bg-white dark:bg-background-dark shadow-md rounded-md"
      >
        <lib-auth-form-header title="Sign In" />
        <form
          [formRoot]="loginForm"
          class="grid grid-cols-1 sm:grid-cols-2 gap-x-3 gap-y-1.5 items-start"
        >
          <!-- Email -->
          <div class="flex flex-col gap-0.5 col-span-1 sm:col-span-2">
            <lib-ui-form-error
              [form]="loginForm"
              ctrlName="email"
              #emailError="formError"
            >
              <lib-ui-form-label label="Email" />
            </lib-ui-form-error>
            <input
              type="email"
              placeholder="you@example.com"
              class="form-control"
              [ngClass]="{
                'form-control-valid': !emailError.hasError,
                'form-control-error': emailError.hasError,
              }"
              [formField]="loginForm.email"
            />
          </div>

          <!-- Password -->
          <div class="flex flex-col gap-0.5 col-span-1 sm:col-span-2 relative">
            <lib-ui-form-error
              [form]="loginForm"
              ctrlName="password"
              #passwordError="formError"
            >
              <lib-ui-form-label label="Password" />
            </lib-ui-form-error>
            <input
              [type]="showPassword() ? 'text' : 'password'"
              id="password"
              placeholder="••••••••"
              class="form-control"
              [ngClass]="{
                'form-control-valid': !passwordError.hasError,
                'form-control-error': passwordError.hasError,
              }"
              [formField]="loginForm.password"
            />
            <!-- Eye Icon -->
            <button
              type="button"
              (click)="toggleShow()"
              class="absolute right-3 top-7.25 text-gray-500 dark:text-gray-400 text-xs focus:outline-none"
              tabindex="-1"
            >
              <fa-icon
                [icon]="showPassword() ? eyeSlashIcon : eyeIcon"
              ></fa-icon>
            </button>
          </div>

          <!-- Submit Button -->
          <div
            class="flex flex-row justify-center gap-2 col-span-1 sm:col-span-2 mt-10 mb-5"
          >
            <button
              type="submit"
              class="text-sm px-4 py-1 rounded-md
            bg-slate-950 text-white cursor-pointer
            dark:bg-white dark:text-slate-950
            hover:bg-slate-800 dark:hover:bg-slate-200"
            >
              Login
            </button>
          </div>
        </form>
      </lib-ui-card>
    </ng-container>
    <lib-ui-loading-indicator [loading]="authStore.loading()" />
  `,
})
export class AdminLoginForm {
  protected readonly eyeIcon = faEye;
  protected readonly eyeSlashIcon = faEyeSlash;
  protected authStore = inject(AuthStore);
  readonly dispatch = injectDispatch(authEvents);

  route = inject(ActivatedRoute);

  showPassword = signal(false);

  loginModel = signal<Login>({ email: '', password: '' });

  loginForm = form(
    this.loginModel,
    (schemaPath) => validateStandardSchema(schemaPath, LoginSchema),
    {
      submission: {
        // ✅ Actually dispatch the login event on valid submission
        action: async (field) => {
          const { email, password } = field().value();
          this.dispatch.adminLogin({ username: email, password });
        },
        onInvalid: (field) => {
          console.error('Login failed — invalid form values:', field().value());
        },
        ignoreValidators: 'none',
      },
    },
  );

  constructor() {
    const redirectUrl =
      this.route.snapshot.queryParamMap.get('redirectUrl') ?? '';
    if (redirectUrl.length > 0) {
      localStorage.setItem(AUTH_STORAGE_KEYS.requestedUrl, redirectUrl);
    }
  }

  toggleShow(): void {
    this.showPassword.set(!this.showPassword());
  }
}
